export type PasteSourceProfile = 'unknown' | 'word-desktop' | 'word-web' | 'outlook';

export type PasteInsertKind = 'html' | 'text' | 'none';

export interface PasteNormalizationInput {
  html?: string;
  text?: string;
  imageFiles?: File[];
  clipboardItems?: ArrayLike<DataTransferItem> | null;
}

export interface PasteNormalizationResult {
  kind: PasteInsertKind;
  content: string;
  sourceProfile: PasteSourceProfile;
  diagnostics: string[];
}

export interface TransformContext {
  document: Document;
  body: HTMLElement;
  input: PasteNormalizationInput;
  sourceProfile: PasteSourceProfile;
  diagnostics: string[];
  imageFiles: File[];
  readFileAsDataUrl: (file: File) => Promise<string>;
}

export type HtmlTransform = (context: TransformContext) => Promise<void> | void;
export type PasteSourceDetector = (input: PasteNormalizationInput) => PasteSourceProfile;

export interface PasteNormalizerOptions {
  detector?: PasteSourceDetector;
  transforms?: HtmlTransform[];
  readFileAsDataUrl?: (file: File) => Promise<string>;
}

function cleanInlineStyles(styleValue: string) {
  const cleaned = styleValue
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => !/^background(?:-color)?\s*:/i.test(part))
    .filter((part) => !/^mso-highlight\s*:/i.test(part))
    .filter((part) => !/^highlight\s*:/i.test(part));

  return cleaned.join('; ');
}

function elementHasVisibleBorder(element: Element) {
  const borderAttr = element.getAttribute('border');
  if (borderAttr && borderAttr !== '0') {
    return true;
  }

  const styleValue = element.getAttribute('style') || '';
  const declarations = styleValue
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean);

  return declarations.some((declaration) => {
    if (!/^border(?:-(top|right|bottom|left))?\s*:/i.test(declaration)) {
      return false;
    }

    return !/\bnone\b|\b0(?:px|pt)?\b/i.test(declaration);
  });
}

function stripBorderStyling(element: Element) {
  element.removeAttribute('border');

  const styleValue = element.getAttribute('style');
  if (!styleValue) return;

  const cleaned = styleValue
    .split(';')
    .map((part) => part.trim())
    .filter(Boolean)
    .filter((part) => !/^mso-border-alt\s*:/i.test(part))
    .filter((part) => !/^border(?:-(top|right|bottom|left))?\s*:/i.test(part))
    .join('; ');

  if (cleaned) {
    element.setAttribute('style', cleaned);
  } else {
    element.removeAttribute('style');
  }
}

function defaultDetectSourceProfile(input: PasteNormalizationInput): PasteSourceProfile {
  const html = input.html || '';

  if (/class\s*=\s*['"][^'"]*Mso/i.test(html) || /xmlns:o=['"]urn:schemas-microsoft-com:office:office/i.test(html)) {
    return 'word-desktop';
  }

  if (/id\s*=\s*['"]docs-internal-guid-/i.test(html) || /docs-internal-guid/i.test(html)) {
    return 'word-web';
  }

  if (/outlook/i.test(html) || /x_apple-data-detectors/i.test(html)) {
    return 'outlook';
  }

  return 'unknown';
}

async function defaultReadFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result || ''));
    reader.onerror = () => reject(new Error(`Failed to read pasted image: ${file.name}`));
    reader.readAsDataURL(file);
  });
}

function removeUnwantedBackgroundStylingTransform(context: TransformContext) {
  const elements = [context.body, ...Array.from(context.body.querySelectorAll('*'))];

  for (const element of elements) {
    element.removeAttribute('bgcolor');

    const styleValue = element.getAttribute('style');
    if (!styleValue) continue;

    const cleanedStyle = cleanInlineStyles(styleValue);
    if (cleanedStyle) {
      element.setAttribute('style', cleanedStyle);
    } else {
      element.removeAttribute('style');
    }
  }

  context.diagnostics.push('removed-background-styling');
}

function stripUniformTableBordersTransform(context: TransformContext) {
  const tables = Array.from(context.body.querySelectorAll('table'));

  for (const table of tables) {
    const cells = Array.from(table.querySelectorAll('td,th'));
    if (cells.length === 0) continue;

    const borderedCellCount = cells.filter((cell) => elementHasVisibleBorder(cell)).length;
    const borderRatio = borderedCellCount / cells.length;
    const rowBorders = Array.from(table.querySelectorAll('tr')).filter((row) => elementHasVisibleBorder(row)).length;
    const hasTableBorder = elementHasVisibleBorder(table);

    const looksLikeUniformGrid =
      borderRatio >= 0.6 ||
      (hasTableBorder && borderRatio >= 0.4) ||
      (rowBorders > 0 && borderRatio >= 0.4);

    if (!looksLikeUniformGrid) continue;

    stripBorderStyling(table);
    table.removeAttribute('cellpadding');
    table.removeAttribute('cellspacing');

    for (const row of Array.from(table.querySelectorAll('tr'))) {
      stripBorderStyling(row);
    }
    for (const cell of cells) {
      stripBorderStyling(cell);
    }
  }

  context.diagnostics.push('stripped-uniform-table-borders');
}

async function appendImageFilesWhenHtmlHasNoImagesTransform(context: TransformContext) {
  if (context.imageFiles.length === 0) {
    return;
  }

  const images = Array.from(context.body.querySelectorAll('img'));
  if (images.length > 0) {
    return;
  }

  for (const imageFile of context.imageFiles) {
    const dataUrl = await context.readFileAsDataUrl(imageFile);
    if (!dataUrl) continue;
    const p = context.document.createElement('p');
    const img = context.document.createElement('img');
    img.setAttribute('src', dataUrl);
    p.appendChild(img);
    context.body.appendChild(p);
  }

  context.diagnostics.push('appended-image-files-to-html');
}

export const defaultHtmlTransforms: HtmlTransform[] = [
  removeUnwantedBackgroundStylingTransform,
  stripUniformTableBordersTransform,
  appendImageFilesWhenHtmlHasNoImagesTransform,
];

export function extractImageFilesFromClipboardItems(
  items: ArrayLike<DataTransferItem> | null | undefined,
): File[] {
  if (!items) {
    return [];
  }

  return Array.from(items)
    .map((item) => (item.kind === 'file' ? item.getAsFile() : null))
    .filter((file): file is File => !!file && file.type.startsWith('image/'));
}

function buildImageHtmlFromFiles(files: File[], readFileAsDataUrl: (file: File) => Promise<string>) {
  return Promise.all(
    files.map(async (file) => {
      const src = await readFileAsDataUrl(file);
      return src ? `<p><img src="${src}" /></p>` : '';
    }),
  ).then((fragments) => fragments.filter(Boolean).join(''));
}

export function createPasteNormalizer(options: PasteNormalizerOptions = {}) {
  const detector = options.detector || defaultDetectSourceProfile;
  const transforms = options.transforms || defaultHtmlTransforms;
  const readFileAsDataUrl = options.readFileAsDataUrl || defaultReadFileAsDataUrl;

  return {
    async normalize(input: PasteNormalizationInput): Promise<PasteNormalizationResult> {
      const html = input.html || '';
      const text = input.text || '';
      const imageFiles = input.imageFiles || extractImageFilesFromClipboardItems(input.clipboardItems);
      const sourceProfile = detector(input);
      const diagnostics = [`profile:${sourceProfile}`];

      if (html.trim()) {
        const parser = new DOMParser();
        const doc = parser.parseFromString(html, 'text/html');
        const body = doc.body;

        const context: TransformContext = {
          document: doc,
          body,
          input,
          sourceProfile,
          diagnostics,
          imageFiles,
          readFileAsDataUrl,
        };

        for (const transform of transforms) {
          await transform(context);
        }

        return {
          kind: 'html',
          content: body.innerHTML,
          sourceProfile,
          diagnostics,
        };
      }

      if (imageFiles.length > 0) {
        const imageHtml = await buildImageHtmlFromFiles(imageFiles, readFileAsDataUrl);
        if (imageHtml) {
          diagnostics.push('built-image-html-from-files');
          return {
            kind: 'html',
            content: imageHtml,
            sourceProfile,
            diagnostics,
          };
        }
      }

      if (text.trim()) {
        diagnostics.push('used-plain-text');
        return {
          kind: 'text',
          content: text,
          sourceProfile,
          diagnostics,
        };
      }

      diagnostics.push('empty-payload');
      return {
        kind: 'none',
        content: '',
        sourceProfile,
        diagnostics,
      };
    },
  };
}

export const pasteNormalizer = createPasteNormalizer();